// offline-debug.js
// Debugging utilities for offline functionality

export const debugOfflineMode = {
  
  // Check if running in offline mode
  isOffline() {
    const isElectron = window && window.electron;
    console.log('Debug: Is Electron environment?', isElectron);
    return isElectron;
  },

  // Test data file accessibility
  async testDataFiles() {
    const files = [
      '/data/users.json',
      '/data/companies.json', 
      '/data/course.json',
      '/data/values.json',
      '/data/manifest.json'
    ];
    
    console.log('Debug: Testing data file accessibility...');
    
    for (const file of files) {
      try {
        const response = await fetch(file);
        if (response.ok) {
          console.log(`✓ ${file} - accessible`);
        } else {
          console.warn(`⚠ ${file} - not accessible (${response.status})`);
        }
      } catch (error) {
        console.error(`✗ ${file} - failed to fetch:`, error.message);
      }
    }
  },

  // Test authentication with sample credentials
  async testAuthentication() {
    console.log('Debug: Testing offline authentication...');
    
    try {
      const { offlineAuthService } = await import('@/services/offline-auth');
      
      // Test with admin credentials
      const result = await offlineAuthService.authenticate('admin@gmail.com', 'any_password');
      console.log('✓ Authentication test successful:', result);
      
      // Test token validation
      const userResult = await offlineAuthService.getUser(result.token);
      console.log('✓ Token validation test successful:', userResult);
      
    } catch (error) {
      console.error('✗ Authentication test failed:', error);
    }
  },

  // Monitor network requests to ensure no live API calls
  monitorNetworkRequests() {
    const originalFetch = window.fetch;
    const originalXHROpen = XMLHttpRequest.prototype.open;
    
    // Track all requests for analysis
    this.requestLog = [];
    
    // Monitor fetch requests
    window.fetch = function(...args) {
      const url = args[0];
      const isLiveCall = typeof url === 'string' && 
                         (url.includes('api.sns.aaibuzz.com') || 
                          (url.startsWith('http') && !url.startsWith('http://localhost') && !url.startsWith('/data/')));
      
      if (isLiveCall) {
        console.warn('⚠ LIVE API CALL DETECTED (fetch):', url);
        console.trace('Request stack trace:');
      } else if (typeof url === 'string' && !url.startsWith('data:')) {
        console.log('✓ Offline request (fetch):', url);
      }
      
      return originalFetch.apply(this, args);
    };
    
    // Monitor XMLHttpRequest
    XMLHttpRequest.prototype.open = function(method, url, ...args) {
      const isLiveCall = typeof url === 'string' && 
                         (url.includes('api.sns.aaibuzz.com') || 
                          (url.startsWith('http') && !url.startsWith('http://localhost') && !url.startsWith('/data/')));
      
      if (isLiveCall) {
        console.warn('⚠ LIVE API CALL DETECTED (XHR):', method, url);
        console.trace('Request stack trace:');
      } else if (typeof url === 'string' && !url.startsWith('data:')) {
        console.log('✓ Offline request (XHR):', method, url);
      }
      
      return originalXHROpen.call(this, method, url, ...args);
    };
    
    console.log('Debug: Enhanced network request monitoring enabled');
    console.log('- Will log all offline requests with ✓');
    console.log('- Will warn about live API calls with ⚠');
  },

  // Complete offline functionality test
  async runCompleteTest() {
    console.log('=== OFFLINE MODE DEBUGGING ===');
    
    console.log('1. Checking offline mode...');
    const isOffline = this.isOffline();
    
    console.log('2. Testing data file accessibility...');
    await this.testDataFiles();
    
    console.log('3. Testing authentication...');
    await this.testAuthentication();
    
    console.log('4. Setting up network monitoring...');
    this.monitorNetworkRequests();
    
    console.log('=== OFFLINE MODE DEBUG COMPLETE ===');
    
    return {
      isOffline,
      timestamp: new Date().toISOString()
    };
  }
};

// Auto-run debug in development
if (process.env.NODE_ENV === 'development') {
  setTimeout(() => {
    debugOfflineMode.runCompleteTest();
  }, 1000);
}